import { t } from '../i18n';
// admin-client/src/components/AdminAssetSummary.jsx
import React from 'react';
import { createPortal } from 'react-dom';
import api from '../api/axios';

function toNumber(value) {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
}

function formatMoney(value) {
    return toNumber(value).toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    });
}

function pickAssetSummary(user) {
    return {
        asset: toNumber(user?.asset),
        rakeback_balance: toNumber(user?.rakeback_balance),
        lounge_point_balance: toNumber(user?.lounge_point_balance),
    };
}

function normalizeAmountInput(value) {
    const raw = String(value ?? '').trim();
    if (!/^\d+(?:\.\d{0,2})?$/.test(raw))
        return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return Math.round(n * 100) / 100;
}

export default function AdminAssetSummary({ className = '', compact = false }) {
    const [summary, setSummary] = React.useState(() => {
        try {
            return pickAssetSummary(JSON.parse(localStorage.getItem('user') || '{}'));
        }
        catch {
            return pickAssetSummary(null);
        }
    });
    const [loading, setLoading] = React.useState(false);
    const [transferOpen, setTransferOpen] = React.useState(false);
    const [transferAmount, setTransferAmount] = React.useState('');
    const [transferLoading, setTransferLoading] = React.useState(false);
    const [transferError, setTransferError] = React.useState('');
    const [transferSuccess, setTransferSuccess] = React.useState('');

    const persistUserBalances = React.useCallback((patch) => {
        try {
            const prev = JSON.parse(localStorage.getItem('user') || '{}');
            localStorage.setItem('user', JSON.stringify({ ...prev, ...patch }));
        }
        catch {
            localStorage.setItem('user', JSON.stringify(patch || {}));
        }
    }, []);

    const refresh = React.useCallback(async () => {
        const token = localStorage.getItem('token');
        if (!token)
            return;
        setLoading(true);
        try {
            const { data } = await api.get('/admin/auth/me');
            const user = data?.user || null;
            const nextSummary = pickAssetSummary(user);
            setSummary(nextSummary);
            if (user) {
                persistUserBalances(user);
            }
        }
        catch (e) {
            // 상단 표시 영역 실패가 페이지 전체 사용을 막으면 안 됨
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('[AdminAssetSummary] refresh failed', e?.response?.data || e?.message || e);
            }
        }
        finally {
            setLoading(false);
        }
    }, [persistUserBalances]);

    React.useEffect(() => {
        refresh();
        const handleRefresh = () => refresh();
        const handleFocus = () => refresh();
        window.addEventListener('admin:asset-summary-refresh', handleRefresh);
        window.addEventListener('focus', handleFocus);
        return () => {
            window.removeEventListener('admin:asset-summary-refresh', handleRefresh);
            window.removeEventListener('focus', handleFocus);
        };
    }, [refresh]);

    const openTransferModal = React.useCallback(() => {
        const available = Math.max(0, toNumber(summary.rakeback_balance));
        if (available <= 0)
            return;
        setTransferAmount(available.toFixed(2));
        setTransferError('');
        setTransferSuccess('');
        setTransferOpen(true);
    }, [summary.rakeback_balance]);

    const closeTransferModal = React.useCallback(() => {
        if (transferLoading)
            return;
        setTransferOpen(false);
        setTransferError('');
    }, [transferLoading]);

    const useAllRakeback = React.useCallback(() => {
        setTransferAmount(Math.max(0, toNumber(summary.rakeback_balance)).toFixed(2));
        setTransferError('');
    }, [summary.rakeback_balance]);

    const submitRakebackTransfer = React.useCallback(async (event) => {
        event?.preventDefault?.();
        if (transferLoading)
            return;

        const amount = normalizeAmountInput(transferAmount);
        const available = Math.max(0, toNumber(summary.rakeback_balance));

        if (amount == null) {
            setTransferError(t("0.01 이상의 금액을 소수 둘째 자리까지 입력해 주세요."));
            return;
        }
        if (amount > available + 0.000001) {
            setTransferError(t("보유한 레이크백보다 큰 금액은 이동할 수 없습니다."));
            return;
        }

        setTransferLoading(true);
        setTransferError('');
        setTransferSuccess('');
        try {
            const { data } = await api.post('/admin/assets/rakeback/convert', {
                amount: amount.toFixed(2),
            });

            const nextSummary = {
                ...summary,
                asset: toNumber(data?.asset),
                rakeback_balance: toNumber(data?.rakeback_balance),
            };
            setSummary(nextSummary);
            persistUserBalances({
                asset: nextSummary.asset,
                rakeback_balance: nextSummary.rakeback_balance,
            });
            setTransferSuccess(t("레이크백을 일반 자산으로 이동했습니다."));
            setTransferOpen(false);
            window.dispatchEvent(new Event('admin:asset-summary-refresh'));
        }
        catch (e) {
            const server = e?.response?.data;
            if (server && (server.asset != null || server.rakeback_balance != null)) {
                setSummary((prev) => ({
                    ...prev,
                    ...(server.asset != null ? { asset: toNumber(server.asset) } : {}),
                    ...(server.rakeback_balance != null ? { rakeback_balance: toNumber(server.rakeback_balance) } : {}),
                }));
            }
            setTransferError(server?.message || e?.message || t("레이크백 이동에 실패했습니다."));
        }
        finally {
            setTransferLoading(false);
        }
    }, [persistUserBalances, summary, transferAmount, transferLoading]);

    const items = [
        { key: 'asset', label: t("일반 자산"), value: summary.asset },
        { key: 'rakeback', label: t("레이크백"), value: summary.rakeback_balance },
        { key: 'lounge', label: t("라운지포인트"), value: summary.lounge_point_balance },
    ];

    const wrapperClassName = [
        compact ? 'flex flex-col gap-2' : 'flex items-center gap-2',
        'rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm',
        className,
    ].filter(Boolean).join(' ');

    return (<>
      <div className={wrapperClassName} aria-label={t("관리자 자산 표시 영역")}>
        {items.map((item, idx) => (<React.Fragment key={item.key}>
            {!compact && idx > 0 && <span className="h-4 w-px bg-gray-200" aria-hidden="true"/>}
            <div className={compact ? 'flex items-center justify-between gap-3 text-sm' : 'flex items-center gap-1.5 whitespace-nowrap text-sm'}>
              <span className={compact ? 'min-w-0 truncate text-gray-500' : 'text-gray-500'}>{item.label}</span>
              <span className={compact ? 'flex-shrink-0 font-semibold text-gray-900' : 'font-semibold text-gray-900'}>{formatMoney(item.value)}</span>
              {item.key === 'rakeback' && (
                <button
                  type="button"
                  onClick={openTransferModal}
                  disabled={loading || transferLoading || toNumber(summary.rakeback_balance) <= 0}
                  className={[
                      compact ? 'ml-2 px-2 py-1 text-xs' : 'ml-1 px-2 py-1 text-[11px]',
                      'rounded-md border border-gray-300 bg-gray-50 font-medium text-gray-700',
                      'transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40',
                  ].join(' ')}
                >
                  {t("자산으로 이동")}
                </button>
              )}
            </div>
          </React.Fragment>))}
        {loading && <span className={compact ? 'h-2 w-2 rounded-full bg-gray-300' : 'ml-1 h-2 w-2 rounded-full bg-gray-300'} title={t("갱신 중")}/>}
        {!compact && transferSuccess && (
          <span className="ml-1 text-[11px] font-medium text-emerald-600">{transferSuccess}</span>
        )}
      </div>

      {transferOpen && typeof document !== 'undefined' && createPortal((
        <div
          className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-black/40 px-4 py-4 sm:items-center sm:py-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-rakeback-transfer-title"
          onMouseDown={(e) => {
              if (e.target === e.currentTarget)
                  closeTransferModal();
          }}
        >
          <form
            onSubmit={submitRakebackTransfer}
            className="my-auto w-full max-w-sm overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl"
            style={{ maxHeight: 'calc(100dvh - 2rem)' }}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 id="admin-rakeback-transfer-title" className="text-base font-semibold text-gray-900">
                  {t("레이크백 → 일반 자산")}
                </h2>
                <p className="mt-1 text-xs leading-5 text-gray-500">
                  {t("이동할 금액만큼 레이크백이 차감되고 일반 자산에 즉시 추가됩니다.")}
                </p>
              </div>
              <button
                type="button"
                onClick={closeTransferModal}
                disabled={transferLoading}
                className="rounded-md px-2 py-1 text-lg leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
                aria-label={t("닫기")}
              >
                ×
              </button>
            </div>

            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-gray-500">{t("보유 레이크백")}</span>
                <span className="font-semibold text-gray-900">{formatMoney(summary.rakeback_balance)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-xs">
                <span className="text-gray-500">{t("현재 일반 자산")}</span>
                <span className="font-semibold text-gray-900">{formatMoney(summary.asset)}</span>
              </div>
            </div>

            <label className="mt-4 block text-xs font-medium text-gray-700">
              {t("이동 금액")}
            </label>
            <div className="mt-1 flex gap-2">
              <input
                autoFocus
                type="number"
                min="0.01"
                step="0.01"
                max={Math.max(0, toNumber(summary.rakeback_balance))}
                value={transferAmount}
                onChange={(e) => {
                    setTransferAmount(e.target.value);
                    setTransferError('');
                }}
                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-500 focus:ring-1 focus:ring-gray-300"
                placeholder="0.00"
                disabled={transferLoading}
              />
              <button
                type="button"
                onClick={useAllRakeback}
                disabled={transferLoading}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {t("전액")}
              </button>
            </div>

            {transferError && (
              <div className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-600">
                {transferError}
              </div>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeTransferModal}
                disabled={transferLoading}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {t("취소")}
              </button>
              <button
                type="submit"
                disabled={transferLoading || toNumber(summary.rakeback_balance) <= 0}
                className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-40"
              >
                {transferLoading ? t("이동 중...") : t("자산으로 이동")}
              </button>
            </div>
          </form>
        </div>
      ), document.body)}
    </>);
}
